import { BetslipType } from '../../../../core/betslip-type';
import { SlipTypeKeys } from '../../../bet-generation/betslip-system-type';
import { IBetslip } from '../../../bet-generation/models';
import { IBetslipTypeState } from '../../../types/state';
import { IBetslipSummary } from '../../models';

const loadingClass = 'betslip-summary--loading';

export function evaluateClasses(
    betslip: IBetslip | undefined,
    betslipSummary: IBetslipSummary | undefined,
    typeState: IBetslipTypeState | undefined,
    teaserSummariesLength: number,
    isLinearBetslip: boolean,
    isSportcastAsComboEnabled: boolean,
): string[] {
    const baseClass = isLinearBetslip ? 'betslip-summary-linear' : 'betslip-summary';
    const classes = evaluateConditionalClasses(betslip, betslipSummary, typeState, teaserSummariesLength, isLinearBetslip, isSportcastAsComboEnabled);

    return [baseClass, ...classes];
}

function evaluateConditionalClasses(
    betslip: IBetslip | undefined,
    betslipSummary: IBetslipSummary | undefined,
    typeState: IBetslipTypeState | undefined,
    teaserSummariesLength: number,
    isLinear: boolean,
    isSportcastAsComboEnabled: boolean,
): string[] {
    if (isLinear) {
        return evaluateClassesForLinear(betslipSummary);
    }

    if (!betslip || !betslipSummary) {
        return [loadingClass];
    }

    if (betslip.slips.length > 0) {
        const type = evaluateBetslipTypeFromSlips(betslip);
        const selectedPicksAmount = betslip.slips.map((x) => Object.values(x.slipPicks).length).reduce((x, y) => x + y);

        return evaluateClassesForBetslipType(betslip, betslipSummary, teaserSummariesLength, type, selectedPicksAmount, isSportcastAsComboEnabled);
    }

    if (typeState) {
        const type = typeState.base.currentSelectedType;
        const selectedPicksAmount = Object.values(typeState.systemBet.picks).filter((p) => p.isSelected).length;

        return evaluateClassesForBetslipType(betslip, betslipSummary, teaserSummariesLength, type, selectedPicksAmount, isSportcastAsComboEnabled);
    }

    return [loadingClass];
}

function evaluateClassesForLinear(betslipSummary: IBetslipSummary | undefined): string[] {
    const totalStake = 'betslip-summary--total-stake';

    const taxationClass = evaluateTaxationClass(betslipSummary);

    return [totalStake, 'betslip-summary--stake', taxationClass];
}

function evaluateClassesForBetslipType(
    betslip: IBetslip,
    betslipSummary: IBetslipSummary,
    teaserSummariesLength: number,
    betslipType: BetslipType | null,
    selectedPicksAmount: number,
    isSportcastAsComboEnabled: boolean,
): string[] {
    let classes: string[];

    if (betslipType === BetslipType.Single) {
        classes = evaluateSingleBetClasses(betslip, betslipSummary, isSportcastAsComboEnabled);
    } else if (betslipType === BetslipType.Combo || betslipType === BetslipType.Teaser) {
        classes = evaluateMultiBetClasses(betslipSummary, teaserSummariesLength);
    } else if (betslipType === BetslipType.System) {
        classes = evaluateSystemBetClasses(selectedPicksAmount);
    } else {
        return [loadingClass];
    }

    const taxationClass = evaluateTaxationClass(betslipSummary);

    return [...classes, taxationClass];
}

function evaluateTaxationClass(betslipSummary: IBetslipSummary | undefined): string {
    const taxation = betslipSummary?.taxation;

    return taxation && (taxation.isPlaceholderTax || taxation.amount?.greaterThan(0))
        ? 'betslip-summary--possible-winnings-net'
        : 'betslip-summary--possible-winnings';
}

function evaluateSingleBetClasses(betslip: IBetslip, betslipSummary: IBetslipSummary, isSportcastAsComboEnabled: boolean): string[] {
    if (betslip.slips.length > 1) {
        // Multi single
        return ['betslip-summary--stake-per-bet', 'betslip-summary--total-stake'];
    }

    if (betslipSummary.betCount > 1) {
        // One single with multiple bets -- show total stake but not stake per bet.
        return ['betslip-summary--stake', 'betslip-summary--total-stake'];
    }

    if (betslipSummary.betCount === 1 && betslipSummary.hasBetBuilder && isSportcastAsComboEnabled) {
        // One sportcast pick on parlay tab.
        return ['betslip-summary--stake', 'betslip-summary--total-odds', 'reward-tokens-module'];
    }

    // One slip single.
    return ['betslip-summary--stake'];
}

function evaluateMultiBetClasses(betslipSummary: IBetslipSummary, teaserSummariesLength: number): string[] {
    //more than one teaser bet show total stake instead of total odds
    const teaserSummariesClass = teaserSummariesLength > 1 ? 'betslip-summary--total-stake' : 'betslip-summary--total-odds';

    // When multiple bets, e.g. EW show total stake.
    // When one bet OR no bets(eg. non combinable picks) only show total odds
    const betCountSummaryClass = betslipSummary.betCount > 1 ? 'betslip-summary--total-stake' : 'betslip-summary--total-odds';

    const rewardTokensClass = 'reward-tokens-module';

    // Combo bet
    // Always show stake
    return [teaserSummariesClass, 'betslip-summary--stake', betCountSummaryClass, rewardTokensClass];
}

function evaluateSystemBetClasses(selectedPicksAmount: number): string[] {
    const stakeClass = selectedPicksAmount > 1 ? 'betslip-summary--stake-per-bet' : 'betslip-summary--stake';

    return [stakeClass, 'betslip-summary--total-stake'];
}

function evaluateBetslipTypeFromSlips(betslip: IBetslip): BetslipType {
    if (betslip.slips.every((s) => s.type.key === SlipTypeKeys.Single)) {
        return BetslipType.Single;
    }

    if (betslip.slips.every((s) => s.type.key === SlipTypeKeys.Combo)) {
        return BetslipType.Combo;
    }

    return BetslipType.System;
}
